import React, { useEffect, useRef, useState } from "react";

import { useNavigate } from "react-router-dom";
import RBImage from "react-bootstrap/Image";
import { Container, Modal, Spinner, Image } from "react-bootstrap";

import "./take-selfie1.css";

import logo2 from "../../../assets/images/logo2.png";
import addplus from "../../../assets/images/add-plus.png";
import backarrow from "../../../assets/images/backarrow.jpg";
import dont from "../../../assets/images/do.png";
import doo from "../../../assets/images/dont.png";
import responsivebg from "../../../assets/images/responsive-bg.png";

import Stepper from "../Stepper";
import Loader from "../../Utility/Loader";
import ImageCrop from "../../cropimage/ImageCrop";
import * as userActions from "../../../actions/user";
import { fileUpload } from "../../../actions/uploadActions";
import { useAlert } from "../../../Context/AlertModalContext";
import { getProfileSelfieImageUrls } from "../../../utils/serverUtils";

export const Selfie = () => {
  const alert = useAlert();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [showDosDontsModal, setShowDosDontsModal] = useState(true);
  const [duplicateImageError, setDuplicateImageError] = useState(false);

  const selectedFileHashesRef = useRef({});
  const fileInputRefs = {
    main: useRef(null),
    first: useRef(null),
    second: useRef(null),
  };

  const [images, setImages] = useState({
    main: { key: null, src: null, fileName: null, loading: 0, imageKey: "main", file: null, actionType: null },
    first: { key: null, src: null, fileName: null, loading: 0, imageKey: "first", file: null, actionType: null },
    second: { key: null, src: null, fileName: null, loading: 0, imageKey: "second", file: null, actionType: null },
  });

  const computeHash = async (file) => {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const hashBuffer = await crypto.subtle.digest("SHA-256", arrayBuffer);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      return hashArray.map(b => b.toString(16).padStart(2, "0")).join("");
    } catch (error) {
      console.error("Error computing file hash:", error);
      // Fallback to file name and size for duplicate detection
      return `${file.name}-${file.size}-${file.lastModified}`;
    }
  };

  // Enhanced file validation
  const validateFile = (file) => {
    // Check file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      throw new Error("Please select a valid image file (JPEG, PNG, or WebP)");
    }

    return true;
  };

  // Cropper Variables
  const cropperRef = useRef(null);
  const [imageToCrop, setImageToCrop] = useState(null);
  const [showCropModal, setShowCropModal] = useState(false);
  const [currentImageKey, setCurrentImageKey] = useState(null);

  const IMAGE_CROP_DIMENSIONS = {
    main: { width: 500, height: 900 },
    first: { width: 600, height: 600 },
    second: { width: 600, height: 600 },
  };
  const { width, height } = IMAGE_CROP_DIMENSIONS[currentImageKey] || { width: 600, height: 600 };

  useEffect(() => {
    const fetchUserImage = async () => {
      try {
        const response = await userActions.getUserMedia();
        if (response) {
          const profileImages = await getProfileSelfieImageUrls(response);

          setImages((prev) => {
            const updatedImages = { ...prev };
            Object.entries(profileImages).forEach(([key, url]) => {
              if (url) {
                updatedImages[key] = {
                  ...updatedImages[key],
                  src: url,
                  actionType: "uploaded"
                };
              }
            });
            return updatedImages;
          });
        }
      } catch (error) {
        console.error("Error fetching user images:", error);
      }
    };

    fetchUserImage();
  }, []);

  // Fix scrolling issues on mobile devices
  useEffect(() => {
    const fixMobileScrolling = () => {
      // Force enable scrolling on mobile
      document.body.style.overflow = 'auto';
      document.body.style.webkitOverflowScrolling = 'touch';
      document.documentElement.style.overflow = 'auto';
      document.documentElement.style.webkitOverflowScrolling = 'touch';

      // Ensure viewport height is correct
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };

    fixMobileScrolling();
    window.addEventListener('resize', fixMobileScrolling);
    window.addEventListener('orientationchange', fixMobileScrolling);

    return () => {
      window.removeEventListener('resize', fixMobileScrolling);
      window.removeEventListener('orientationchange', fixMobileScrolling);
    };
  }, []);

  const handleClick = imageKey => {
    const input = fileInputRefs[imageKey]?.current;
    if (input) {
      input.value = ""; // Clear the previous file
      input.click();
    }
  };

  const handleFileChange = async (e, imageKey) => {
    const files = e.target?.files;
    if (!files?.length) return;

    const file = files[0];

    try {
      // Validate file
      validateFile(file);

      // Check for duplicate image by hash
      const fileHash = await computeHash(file);
      const isDuplicate = Object.entries(selectedFileHashesRef.current).some(
        ([key, hash]) => key !== imageKey && hash === fileHash
      );

      if (isDuplicate) {
        setDuplicateImageError(true);
        return;
      }

      setDuplicateImageError(false);

      // Save hash in both ref and state (for rendering)
      selectedFileHashesRef.current[imageKey] = fileHash;

      // Read image as base64 for cropping
      const reader = new FileReader();
      reader.onload = () => {
        setImageToCrop(reader.result);
      };
      reader.onerror = () => {
        throw new Error("Failed to read image file");
      };
      reader.readAsDataURL(file);

      setCurrentImageKey(imageKey);
      setShowCropModal(true);
    } catch (error) {
      console.error("File validation error:", error);
      alert.setModal({
        show: true,
        title: "Invalid File",
        message: error.message || "Please select a valid image file.",
      });
      // Clear the input
      e.target.value = "";
    }
  };

  const handleCropCancel = () => {
    setCurrentImageKey(null);
    setShowCropModal(false);
    setImageToCrop(null);
    // Clear the file input for the current image
    if (currentImageKey && fileInputRefs[currentImageKey]?.current) {
      fileInputRefs[currentImageKey].current.value = "";
    }
  };

  const handleCropSave = async () => {
    try {
      if (!cropperRef.current) {
        throw new Error("Cropper not initialized");
      }


      const croppedBlob = await cropperRef.current.handleSubmit();
      if (!croppedBlob) {
        throw new Error("Failed to crop image. Please try again.");
      }

      const croppedFile = new File([croppedBlob], "cropped-image.jpg", {
        type: "image/jpeg",
      });

      const fileURL = URL.createObjectURL(croppedFile);

      // Set loading state
      setImages((prev) => ({
        ...prev,
        [currentImageKey]: {
          ...prev[currentImageKey],
          croppedFile,
          fileName: croppedFile.name,
          src: fileURL,
          loading: -1,
        },
      }));

      handleCropCancel();

      try {
        const uploadedFile = await fileUpload(croppedFile);
        setImages((prev) => ({
          ...prev,
          [currentImageKey]: {
            ...prev[currentImageKey],
            key: uploadedFile.key,
            loading: 0,
          },
        }));
      } catch (error) {
        setImages((prev) => ({
          ...prev,
          [currentImageKey]: {
            ...prev[currentImageKey],
            loading: 0,
          },
        }));
        alert.setModal({
          show: true,
          title: "Upload Failed",
          message: error.message || "Failed to upload image. Please check your internet connection and try again.",
        });
      }
    } catch (error) {
      console.error("Error saving images:", error);
      alert.setModal({
        show: true,
        title: "Crop Error",
        message: error.message || "Something went wrong while cropping. Please try again.",
      });
    }
  };

  const handleNextClick = async () => {
    const requiredKeys = ["main", "first", "second"];

    // Only block if image is completely missing OR hasn't finished uploading
    const isAnyImageMissing = requiredKeys.some((key) => {
      const image = images[key];
      const hasSrc = !!image?.src;
      const hasKey = !!image?.key;
      const isAlreadyUploaded = image?.actionType === "uploaded";

      // Block if: No src at all, OR src exists but no key and not already uploaded
      return !hasSrc || (!hasKey && !isAlreadyUploaded);
    });

    if (isAnyImageMissing) {
      alert.setModal({
        show: true,
        title: "Missing Images",
        message: "Please upload all three images before continuing.",
      });
      return;
    }

    setLoading(true);

    // Only include newly uploaded images (those that have `key`)
    const payload = {};
    requiredKeys.forEach((key) => {
      if (images[key]?.key) {
        payload[key] = images[key].key;
      }
    });

    try {
      if (Object.keys(payload).length > 0) {
        await userActions.updateUserMedia(payload);
      }
      navigate("/located");
    } catch (error) {
      console.error("Error saving images:", error);
      alert.setModal({
        show: true,
        title: "Save Failed",
        message: "Failed to save your images. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const renderImageContent = (img) => (
    <>
      {!img.src ? (
        <>
          <RBImage className="selfie-icon" src={addplus} />
          <span>Add your {img.imageKey} photo</span>
        </>
      ) : (
        <RBImage
          src={img.src}
          className="user-picture1"
          style={{ height: "100%", width: "100%", objectFit: "contain" }}
          alt="Uploaded"
          fluid
        />
      )}

      {/* Enhanced Loading State */}
      {img.loading === -1 && (
        <div className="image-upload-overlay">
          <div className="upload-progress-container">
            <div className="upload-spinner">
              <div className="spinner-ring"></div>
            </div>
            <div className="upload-text">
              <p className="upload-title">Processing Image...</p>
              <p className="upload-subtitle">Please wait while we optimize your photo</p>
            </div>
            <div className="upload-progress-bar">
              <div className="upload-progress-fill"></div>
            </div>
          </div>
        </div>
      )}
    </>
  );

  return (
    <div className="selfie-container">
      {loading && <Loader />}
      <div className="selfie-bg">
        <Image className="banner-image" src={responsivebg} alt="Background" />
      </div>
      <Container className="selfie-main">
        <Container className="selfie-content">

          <Container className="logo-progressbar4">
            <Container className="logo-arrow4">
              <RBImage src={backarrow} className="backarrow" onClick={() => window.history.back()} />
              <RBImage src={logo2} alt="Logo" className="logo" style={{ backgroundColor: "transparent" }} />
            </Container>
            <Stepper activeStep="Selfie" />
          </Container>
          <Container className="selfie-text d-flex flex-column align-items-center">
            <p className="pic-heading mt-2 mb-2">Add your photos</p>
            <p className="pic-text" style={{ fontSize: "16px", marginBottom: "12px" }}>
              Always use photos that clearly show your face! Want more tips?
              <span
                onClick={() => setShowDosDontsModal(true)}
                style={{
                  textDecoration: "underline",
                  color: "blue",
                  cursor: "pointer",
                }}
              > Check them out here.</span>
            </p>
          </Container>

          <Container className="selfie-details">
            <div className="containerChild1">
              <Container className="selfie-adding">
                <div className="selfie-row" onClick={() => handleClick("main")}>
                  <div className="selfie-box1" style={{ height: "24dvh", position: "relative" }}>
                    {renderImageContent(images.main)}
                    <input
                      type="file"
                      ref={fileInputRefs.main}
                      onChange={(e) => handleFileChange(e, "main")}
                      style={{ display: "none" }}
                      accept="image/jpeg,image/jpg,image/png,image/webp"
                    />
                  </div>
                </div>

                <div className="selfie-row2">
                  {["first", "second"].map((key) => (
                    <div
                      key={key}
                      className="selfie-box2"
                      style={{ height: "24dvh", position: "relative" }}
                      onClick={() => handleClick(key)}
                    >
                      {renderImageContent(images[key])}
                      <input
                        type="file"
                        ref={fileInputRefs[key]}
                        onChange={(e) => handleFileChange(e, key)}
                        style={{ display: "none" }}
                        accept="image/jpeg,image/jpg,image/png,image/webp"
                      />
                    </div>
                  ))}
                </div>
              </Container>
            </div>

            {duplicateImageError && (
              <div className="duplicate-image-error">
                <div className="error-message">
                  <span>Please add an image you haven't already used.</span>
                </div>
              </div>
            )}

            <div className="d-flex justify-content-center selfie_next_btn">
              <button
                type="submit"
                className="global-next-bottom-fix-btn"
                onClick={handleNextClick}
                disabled={loading}
              >
                {loading ? (
                  <Spinner animation="border" size="sm" role="status" />
                ) : (
                  "Next"
                )}
              </button>
            </div>
          </Container>
        </Container>
      </Container>

      {/* Image Crop Modal - Shows when user clicks to upload image */}
      <ImageCrop
        ref={cropperRef}
        show={showCropModal}
        onHide={handleCropCancel}
        url={imageToCrop}
        cropWidth={width}
        cropHeight={height}
        onCropSave={handleCropSave}
        onCropCancel={handleCropCancel}
      />

      <Modal
        centered
        className="customer-modal-without-header-footer dos-donts-modal"
        show={showDosDontsModal}
        onHide={() => setShowDosDontsModal(false)}
      >
        <Modal.Header className="do-donts-head" closeButton></Modal.Header>
        <Modal.Body className="dos-donts-modal-body">
          <div style={{ display: "flex", flexDirection: "column" }}>
            <div
              style={{
                alignItems: "center",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <h5>Do's</h5>
              <RBImage
                style={{ marginBottom: "-20px", zIndex: "10" }}
                src={doo}
              />
              <ul className="dos">
                <li>
                  <p>
                    <span>Light it up</span>: Use good lighting, natural light
                    is best. Avoid shadows.
                  </p>
                </li>
                <li>
                  <p>
                    <span>Keep it real</span>: Use authentic pics of yourself
                    which clearly show your face.
                  </p>
                </li>
                <li>
                  <p>
                    <span>Show off your personality</span>: Love traveling?
                    Have a special hobby? Don't hesitate to show it off
                    through your pics.
                  </p>
                </li>
              </ul>
            </div>
            <div
              style={{
                alignItems: "center",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <h5>Don't</h5>
              <RBImage
                style={{ marginBottom: "-20px", zIndex: "10" }}
                src={dont}
              />
              <ul className="dos">
                <li>
                  <p>
                    <span>Hide your face</span>: Ensure your face is clearly
                    visible. Avoid sunglasses or hats that hide your face.
                  </p>
                </li>
                <li>
                  <p>
                    <span>Fake it</span> Avoid heavy filters or overly edited
                    photos. Stay authentic to how you look in real life.
                    You're fabulous as you are!
                  </p>
                </li>
                <li>
                  <p>
                    <span>Add too many pics with others</span>: Group photos
                    can be confusing as members might not know who you are.
                    Solo pics are ideal!
                  </p>
                </li>
              </ul>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};